/**
 * Shooting sports administration.
 *
 * Everything a committee decides once and a desk then lives with: the
 * sports the club runs, the ground, the hours, the waiver, the prices,
 * the rack, and which golf holes the shot goes over.
 *
 * Read-only for anybody below manager rather than hidden from them. The
 * desk is asked "how old do you have to be" and "what does a round
 * cost" every Saturday, and a screen that refuses to answer because the
 * person asking cannot also change it is a screen that sends them to
 * find somebody who can.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Status } from '../components/ui.tsx';
import { money } from '../lib/format.ts';

/* Shapes */

interface Sport {
  id: string;
  code: string;
  name: string;
  kind: string;
  governing_body: string | null;
  requires_waiver: boolean;
  minimum_age: number | null;
  supervised_age: number | null;
  is_active: boolean;
  fields: number;
  fields_available: number;
  notes: string | null;
}

interface Field {
  id: string;
  sport_id: string;
  sport_name: string;
  code: string;
  name: string;
  layout: string;
  station_count: number;
  squad_size: number;
  status: string;
  notes: string | null;
}

interface Season {
  id: string;
  sport_id: string;
  sport_name: string;
  name: string;
  starts_on: string;
  ends_on: string;
  open_days: number[];
  session_minutes: number;
  first_start_at: string;
  last_start_at: string;
  last_start_at_unlit: string | null;
  note: string | null;
}

interface Holiday {
  id: string;
  holiday_on: string;
  name: string;
}

interface Waiver {
  id: string;
  sport_id: string;
  sport_name: string;
  version: number;
  title: string;
  effective_from: string;
  effective_to: string | null;
  renew_months: number | null;
  in_force: boolean;
  signatures: number;
  body?: string;
}

interface Rate {
  id: string;
  sport_name: string;
  field_name: string | null;
  item: string;
  rate_class: string;
  day_type: string | null;
  unit: string;
  amount_minor: string | number;
  revenue_category: string;
  effective_from: string;
  effective_to: string | null;
  in_force: boolean;
}

interface Equipment {
  id: string;
  code: string;
  name: string;
  kind: string;
  gauge: string | null;
  status: string;
  rate_item: string;
  has_serial: boolean;
  out_to: string | null;
  due_back_at: string | null;
  overdue: boolean;
}

interface Conflict {
  id: string;
  field_name: string;
  course_name: string;
  playing_number: number | null;
  par: number | null;
  exposure_from_minutes: number;
  exposure_to_minutes: number;
  blocks_tee_times: boolean;
  publishes_notice: boolean;
  note: string | null;
}

type Tab = 'sports' | 'ground' | 'hours' | 'waiver' | 'rates' | 'rack' | 'golf';

const TABS: { id: Tab; label: string }[] = [
  { id: 'sports', label: 'Sports' },
  { id: 'ground', label: 'Fields' },
  { id: 'hours', label: 'Hours & Seasons' },
  { id: 'waiver', label: 'The Waiver' },
  { id: 'rates', label: 'Rate Card' },
  { id: 'rack', label: 'Guns & Kit' },
  { id: 'golf', label: 'The Golf Course' },
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ITEM_LABEL: Record<string, string> = {
  range_fee: 'Station time',
  clays: 'Clays',
  ammunition: 'Ammunition',
  loaner_firearm: 'Gun hire',
  loaner_ppe: 'Eyes & ears',
  guest_fee: 'Guest fee',
  instruction: 'Instruction',
};
const UNIT_LABEL: Record<string, string> = {
  session: 'per session',
  round: 'per round of 25',
  box: 'per box of 25',
  item: 'each',
  hour: 'per hour',
};

const hhmm = (t: string) => t.slice(0, 5);
const days = (d: number[]) => d.map((n) => DAY_NAMES[n - 1]).join(', ');

/* ------------------------------------------------------------------ */

export function ShootingAdminPage() {
  const { atLeast, currency } = useSession();
  const canEdit = atLeast('manager');
  const [tab, setTab] = useState<Tab>('sports');
  const [note, setNote] = useState<string | null>(null);

  const sports = useApi<{ sports: Sport[] }>('/v1/shooting/sports?include_inactive=true');
  const fields = useApi<{ fields: Field[] }>('/v1/shooting/fields?include_retired=true');
  const seasons = useApi<{ seasons: Season[]; holidays: Holiday[] }>('/v1/shooting/seasons');
  const waivers = useApi<{ waivers: Waiver[] }>('/v1/shooting/waivers');
  const rates = useApi<{ rates: Rate[] }>('/v1/shooting/rates');
  const rack = useApi<{ equipment: Equipment[] }>('/v1/shooting/equipment?include_retired=true');
  const conflicts = useApi<{ conflicts: Conflict[] }>('/v1/shooting/conflicts');

  const done = (message: string, reload: () => void) => {
    setNote(message);
    reload();
  };

  return (
    <>
      <Card
        title="Shooting Sports"
        actions={
          <span className="muted small">
            {canEdit ? 'What the committee decided' : 'Read only — a manager changes these'}
          </span>
        }
      >
        <div className="row">
          {TABS.map((t) => (
            <button
              key={t.id}
              className={t.id === tab ? 'gold small' : 'secondary small'}
              onClick={() => setTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Card>

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {tab === 'sports' && (
        <SportsTab
          query={sports}
          canEdit={canEdit}
          onDone={(m) => done(m, sports.reload)}
        />
      )}

      {tab === 'ground' && (
        <FieldsTab
          query={fields}
          sports={sports.data?.sports ?? []}
          canEdit={canEdit}
          onDone={(m) => {
            done(m, fields.reload);
            sports.reload();
          }}
        />
      )}

      {tab === 'hours' && (
        <HoursTab
          query={seasons}
          sports={sports.data?.sports ?? []}
          canEdit={canEdit}
          onDone={(m) => done(m, seasons.reload)}
        />
      )}

      {tab === 'waiver' && (
        <WaiverTab
          query={waivers}
          sports={sports.data?.sports ?? []}
          canEdit={canEdit}
          onDone={(m) => done(m, waivers.reload)}
        />
      )}

      {tab === 'rates' && (
        <RatesTab
          query={rates}
          sports={sports.data?.sports ?? []}
          currency={currency}
          canEdit={canEdit}
          onDone={(m) => done(m, rates.reload)}
        />
      )}

      {tab === 'rack' && (
        <RackTab
          query={rack}
          sports={sports.data?.sports ?? []}
          canEdit={canEdit}
          onDone={(m) => done(m, rack.reload)}
        />
      )}

      {tab === 'golf' && <GolfTab query={conflicts} />}
    </>
  );
}

/* ------------------------------------------------------------------ */

function SportsTab({
  query,
  canEdit,
  onDone,
}: {
  query: ReturnType<typeof useApi<{ sports: Sport[] }>>;
  canEdit: boolean;
  onDone: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    code: '',
    name: '',
    minimum_age: '',
    supervised_age: '',
    requires_waiver: true,
    governing_body: '',
  });

  const create = useMutation(async () => {
    const r = await api<{ sport: Sport; note?: string }>('/v1/shooting/sports', {
      method: 'POST',
      body: {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        requires_waiver: form.requires_waiver,
        minimum_age: form.minimum_age ? Number(form.minimum_age) : null,
        supervised_age: form.supervised_age ? Number(form.supervised_age) : null,
        governing_body: form.governing_body.trim() || null,
      },
    });
    setAdding(false);
    setForm({
      code: '',
      name: '',
      minimum_age: '',
      supervised_age: '',
      requires_waiver: true,
      governing_body: '',
    });
    onDone(`${r.sport.name} added.${r.note ? ` ${r.note}` : ''}`);
  });

  const rows = query.data?.sports ?? [];

  return (
    <>
      {adding && (
        <Card title="A Sport the Club Runs">
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>Code</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="SKEET"
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Skeet &amp; Trap"
              />
            </label>
            <label className="field">
              <span>Nobody under</span>
              <input
                type="number"
                value={form.minimum_age}
                onChange={(e) => setForm({ ...form, minimum_age: e.target.value })}
                placeholder="12"
              />
            </label>
            <label className="field">
              <span>Unaccompanied from</span>
              <input
                type="number"
                value={form.supervised_age}
                onChange={(e) => setForm({ ...form, supervised_age: e.target.value })}
                placeholder="16"
              />
            </label>
            <label className="field">
              <span>Governing body</span>
              <input
                value={form.governing_body}
                onChange={(e) => setForm({ ...form, governing_body: e.target.value })}
                placeholder="NSSA"
              />
            </label>
            <label className="field">
              <span>Waiver required</span>
              <select
                value={form.requires_waiver ? 'yes' : 'no'}
                onChange={(e) => setForm({ ...form, requires_waiver: e.target.value === 'yes' })}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          </div>
          <div className="hint">
            Both ages are the club's own, not a governing body's. Below the first, no; between
            the two, only with a named adult on the squad.
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button disabled={create.busy} onClick={() => create.run()}>
              {create.busy ? 'Working…' : 'Add the sport'}
            </button>
            <button className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card
        title="What the Club Runs"
        actions={
          canEdit && !adding ? (
            <button className="secondary small" onClick={() => setAdding(true)}>
              Add a sport
            </button>
          ) : undefined
        }
        flush
      >
        <ErrorBanner error={query.error} />
        {query.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            No shooting sports yet. Adding one is a code, a name, and the two ages the club's
            insurer wrote down.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Code</th>
                <th>Sport</th>
                <th style={{ width: 210 }}>Ages</th>
                <th style={{ width: 120 }}>Waiver</th>
                <th style={{ width: 100 }}>Fields</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} style={s.is_active ? undefined : { opacity: 0.55 }}>
                  <td className="mono">{s.code}</td>
                  <td>
                    {s.name}
                    {s.governing_body && <div className="muted small">{s.governing_body}</div>}
                  </td>
                  <td className="muted small">
                    {s.minimum_age ? `${s.minimum_age} and over` : 'No minimum set'}
                    {s.supervised_age ? `, unaccompanied from ${s.supervised_age}` : ''}
                  </td>
                  <td>
                    {s.requires_waiver ? (
                      <Status tone="good">Required</Status>
                    ) : (
                      <span className="muted small">Not required</span>
                    )}
                  </td>
                  <td className="mono">
                    {s.fields_available} of {s.fields}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function FieldsTab({
  query,
  sports,
  canEdit,
  onDone,
}: {
  query: ReturnType<typeof useApi<{ fields: Field[] }>>;
  sports: Sport[];
  canEdit: boolean;
  onDone: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    sport_id: '',
    code: '',
    name: '',
    layout: 'skeet',
    station_count: '8',
    squad_size: '5',
  });

  const create = useMutation(async () => {
    const r = await api<{ field: Field }>('/v1/shooting/fields', {
      method: 'POST',
      body: {
        sport_id: form.sport_id || sports[0]?.id,
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        layout: form.layout,
        station_count: Number(form.station_count),
        squad_size: Number(form.squad_size),
      },
    });
    setAdding(false);
    onDone(`${r.field.name} added.`);
  });

  const retire = useMutation(async (id: string) => {
    await api(`/v1/shooting/fields/${id}`, { method: 'PATCH', body: { status: 'retired' } });
    onDone('Taken out of service. Sessions already on the sheet are untouched.');
  });

  const rows = query.data?.fields ?? [];

  return (
    <>
      {adding && (
        <Card title="A Piece of Ground">
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>Sport</span>
              <select
                value={form.sport_id}
                onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
              >
                <option value="">Choose…</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Layout</span>
              <select
                value={form.layout}
                onChange={(e) => setForm({ ...form, layout: e.target.value })}
              >
                <option value="skeet">Skeet</option>
                <option value="trap">Trap</option>
                <option value="five_stand">Five-stand</option>
                <option value="sporting_clays">Sporting clays</option>
                <option value="pattern_board">Pattern board</option>
                <option value="archery_butts">Archery butts</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="field">
              <span>Code</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="SKEET_1"
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Skeet Field"
              />
            </label>
            <label className="field">
              <span>Stations</span>
              <input
                type="number"
                value={form.station_count}
                onChange={(e) => setForm({ ...form, station_count: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Squad size</span>
              <input
                type="number"
                value={form.squad_size}
                onChange={(e) => setForm({ ...form, squad_size: e.target.value })}
              />
            </label>
          </div>
          <div className="hint">
            A squad may not be larger than the field has places to stand. The extra shooter
            stands behind somebody who is swinging a loaded gun.
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button disabled={create.busy} onClick={() => create.run()}>
              {create.busy ? 'Working…' : 'Add the field'}
            </button>
            <button className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card
        title="The Ground"
        actions={
          canEdit && !adding && sports.length > 0 ? (
            <button className="secondary small" onClick={() => setAdding(true)}>
              Add a field
            </button>
          ) : undefined
        }
        flush
      >
        <ErrorBanner error={query.error ?? retire.error} />
        {query.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>No fields yet. A field is one place a squad stands and shoots one way.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Code</th>
                <th>Field</th>
                <th style={{ width: 140 }}>Layout</th>
                <th style={{ width: 150 }}>Squad</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((f) => (
                <tr key={f.id} style={f.status === 'retired' ? { opacity: 0.55 } : undefined}>
                  <td className="mono">{f.code}</td>
                  <td>
                    {f.name}
                    <div className="muted small">{f.sport_name}</div>
                  </td>
                  <td className="muted small">{f.layout.replace('_', ' ')}</td>
                  <td className="mono">
                    {f.squad_size} of {f.station_count} stations
                  </td>
                  <td>
                    {f.status === 'available' ? (
                      <Status tone="good">In service</Status>
                    ) : f.status === 'maintenance' ? (
                      <Status tone="warning">Maintenance</Status>
                    ) : (
                      <Status tone="neutral">Retired</Status>
                    )}
                  </td>
                  <td className="actions">
                    {canEdit && f.status !== 'retired' && (
                      <ConfirmButton
                        label="Retire"
                        confirmLabel={`Take ${f.name} out of service`}
                        busy={retire.busy}
                        onConfirm={() => retire.run(f.id)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function HoursTab({
  query,
  sports,
  canEdit,
  onDone,
}: {
  query: ReturnType<typeof useApi<{ seasons: Season[]; holidays: Holiday[] }>>;
  sports: Sport[];
  canEdit: boolean;
  onDone: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    sport_id: '',
    name: '',
    starts_on: '',
    ends_on: '',
    open_days: [3, 6, 7] as number[],
    session_minutes: '60',
    first_start_at: '10:00',
    last_start_at: '16:00',
  });

  const create = useMutation(async () => {
    const r = await api<{ season: Season; note?: string }>('/v1/shooting/seasons', {
      method: 'POST',
      body: {
        sport_id: form.sport_id || sports[0]?.id,
        name: form.name.trim(),
        starts_on: form.starts_on,
        ends_on: form.ends_on,
        open_days: form.open_days,
        session_minutes: Number(form.session_minutes),
        first_start_at: form.first_start_at,
        last_start_at: form.last_start_at,
      },
    });
    setAdding(false);
    onDone(`${r.season.name} added.${r.note ? ` ${r.note}` : ''}`);
  });

  const toggleDay = (n: number) =>
    setForm((f) => ({
      ...f,
      open_days: f.open_days.includes(n)
        ? f.open_days.filter((d) => d !== n)
        : [...f.open_days, n].sort(),
    }));

  const seasons = query.data?.seasons ?? [];
  const holidays = query.data?.holidays ?? [];

  return (
    <>
      {adding && (
        <Card title="When the Range Runs">
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>Sport</span>
              <select
                value={form.sport_id}
                onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
              >
                <option value="">Choose…</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Summer 2027"
              />
            </label>
            <label className="field">
              <span>From</span>
              <input
                type="date"
                value={form.starts_on}
                onChange={(e) => setForm({ ...form, starts_on: e.target.value })}
              />
            </label>
            <label className="field">
              <span>To</span>
              <input
                type="date"
                value={form.ends_on}
                onChange={(e) => setForm({ ...form, ends_on: e.target.value })}
              />
            </label>
            <label className="field">
              <span>First squad at</span>
              <input
                type="time"
                value={form.first_start_at}
                onChange={(e) => setForm({ ...form, first_start_at: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Last squad at</span>
              <input
                type="time"
                value={form.last_start_at}
                onChange={(e) => setForm({ ...form, last_start_at: e.target.value })}
              />
            </label>
          </div>

          <div className="field-block">
            <span className="field-label">Days it runs</span>
            <div className="row">
              {DAY_NAMES.map((d, i) => (
                <button
                  key={d}
                  className={form.open_days.includes(i + 1) ? 'gold small' : 'secondary small'}
                  onClick={() => toggleDay(i + 1)}
                >
                  {d}
                </button>
              ))}
            </div>
            <div className="hint">
              A range needs somebody with a range-safety certificate standing on it, and a club
              has one or two of those. Three days is the usual answer.
            </div>
          </div>

          <div className="actions" style={{ marginTop: 12 }}>
            <button disabled={create.busy} onClick={() => create.run()}>
              {create.busy ? 'Working…' : 'Add the season'}
            </button>
            <button className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card
        title="Seasons"
        actions={
          canEdit && !adding && sports.length > 0 ? (
            <button className="secondary small" onClick={() => setAdding(true)}>
              Add a season
            </button>
          ) : undefined
        }
        flush
      >
        <ErrorBanner error={query.error} />
        {query.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : seasons.length === 0 ? (
          <Empty>
            No seasons yet. Until there is one, the sheet has nothing to generate a day from.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Season</th>
                <th style={{ width: 210 }}>Dates</th>
                <th style={{ width: 200 }}>Days</th>
                <th style={{ width: 190 }}>Hours</th>
              </tr>
            </thead>
            <tbody>
              {seasons.map((s) => (
                <tr key={s.id}>
                  <td>
                    {s.name}
                    <div className="muted small">{s.sport_name}</div>
                  </td>
                  <td className="mono">
                    {s.starts_on} — {s.ends_on}
                  </td>
                  <td className="muted small">{days(s.open_days)}</td>
                  <td className="mono">
                    {hhmm(s.first_start_at)}–{hhmm(s.last_start_at)}
                    <div className="muted small">{s.session_minutes} minute squads</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Days Priced as a Weekend" flush>
        {holidays.length === 0 ? (
          <Empty>None. The range prices Saturday and Sunday as a weekend and nothing else.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 160 }}>Date</th>
                <th>Occasion</th>
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => (
                <tr key={h.id}>
                  <td className="mono">{h.holiday_on}</td>
                  <td>{h.name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function WaiverTab({
  query,
  sports,
  canEdit,
  onDone,
}: {
  query: ReturnType<typeof useApi<{ waivers: Waiver[] }>>;
  sports: Sport[];
  canEdit: boolean;
  onDone: (m: string) => void;
}) {
  const [issuing, setIssuing] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [form, setForm] = useState({
    sport_id: '',
    title: '',
    body: '',
    effective_from: '',
    renew_months: '12',
  });

  const outstanding = useApi<{
    standing: { acceptance_id: string; person_label: string; sport_name: string; version: number }[];
  }>('/v1/shooting/waivers/standing?outstanding=true');

  const full = useApi<{ waivers: Waiver[] }>(
    open ? '/v1/shooting/waivers?include_body=true' : null,
    [open],
  );

  const issue = useMutation(async () => {
    const r = await api<{ waiver: Waiver; must_sign_again: number; note?: string }>(
      '/v1/shooting/waivers',
      {
        method: 'POST',
        body: {
          sport_id: form.sport_id || sports[0]?.id,
          title: form.title.trim(),
          body: form.body,
          effective_from: form.effective_from,
          renew_months: form.renew_months ? Number(form.renew_months) : null,
        },
      },
    );
    setIssuing(false);
    onDone(`Version ${r.waiver.version} issued.${r.note ? ` ${r.note}` : ''}`);
  });

  const rows = query.data?.waivers ?? [];

  return (
    <>
      {issuing && (
        <Card title="A New Version of the Waiver">
          <ErrorBanner error={issue.error} />
          <div className="grid-2">
            <label className="field">
              <span>Sport</span>
              <select
                value={form.sport_id}
                onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
              >
                <option value="">Choose…</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>In force from</span>
              <input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Title</span>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Shooting Sports Waiver and Range Rules"
              />
            </label>
            <label className="field">
              <span>Signed again every (months)</span>
              <input
                type="number"
                value={form.renew_months}
                onChange={(e) => setForm({ ...form, renew_months: e.target.value })}
                placeholder="12"
              />
            </label>
          </div>
          <label className="field-block">
            <span className="field-label">The words</span>
            <textarea
              rows={10}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="What the member is putting their name to."
            />
          </label>
          <div className="hint">
            Issuing a new version closes the one before it and everybody signs again. The old
            text is kept, because the question asked after an accident is what was on the
            clipboard that day.
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button disabled={issue.busy} onClick={() => issue.run()}>
              {issue.busy ? 'Working…' : 'Put it in force'}
            </button>
            <button className="secondary" onClick={() => setIssuing(false)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card
        title="The Documents"
        actions={
          canEdit && !issuing && sports.length > 0 ? (
            <button className="secondary small" onClick={() => setIssuing(true)}>
              Issue a new version
            </button>
          ) : undefined
        }
        flush
      >
        <ErrorBanner error={query.error} />
        {query.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={3} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            No waiver yet. A sport that requires one and has none in force refuses everybody,
            and it refuses them on a Saturday.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 70 }}>Ver</th>
                <th>Document</th>
                <th style={{ width: 210 }}>In force</th>
                <th style={{ width: 110 }}>Signed by</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.id} style={w.in_force ? undefined : { opacity: 0.6 }}>
                  <td className="mono">{w.version}</td>
                  <td>
                    {w.title}
                    <div className="muted small">{w.sport_name}</div>
                    {open === w.id && (
                      <p className="bylaw__body" style={{ whiteSpace: 'pre-wrap' }}>
                        {full.data?.waivers.find((x) => x.id === w.id)?.body ?? 'Loading…'}
                      </p>
                    )}
                  </td>
                  <td className="mono">
                    {w.effective_from} — {w.effective_to ?? 'current'}
                  </td>
                  <td className="mono">{w.signatures}</td>
                  <td className="actions">
                    <button
                      className="secondary small"
                      onClick={() => setOpen(open === w.id ? null : w.id)}
                    >
                      {open === w.id ? 'Hide' : 'Read'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card title="Signatures That No Longer Stand" flush>
        {(outstanding.data?.standing ?? []).length === 0 ? (
          <Empty>Everybody who has signed is current.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Who</th>
                <th style={{ width: 200 }}>Sport</th>
                <th style={{ width: 90 }}>Signed</th>
              </tr>
            </thead>
            <tbody>
              {(outstanding.data?.standing ?? []).map((s) => (
                <tr key={s.acceptance_id}>
                  <td>{s.person_label}</td>
                  <td className="muted small">{s.sport_name}</td>
                  <td className="mono">v{s.version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function RatesTab({
  query,
  sports,
  currency,
  canEdit,
  onDone,
}: {
  query: ReturnType<typeof useApi<{ rates: Rate[] }>>;
  sports: Sport[];
  currency: { code: string; symbol: string | null; exponent: number };
  canEdit: boolean;
  onDone: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    sport_id: '',
    item: 'clays',
    rate_class: 'member',
    unit: 'round',
    amount: '',
    revenue_category: '',
    effective_from: '',
  });

  const categories = useApi<{ categories: { revenue_category: string }[] }>(
    '/v1/revenue-categories',
  );

  const create = useMutation(async () => {
    await api('/v1/shooting/rates', {
      method: 'POST',
      body: {
        sport_id: form.sport_id || sports[0]?.id,
        item: form.item,
        rate_class: form.rate_class,
        unit: form.unit,
        amount_minor: Math.round(Number(form.amount) * 10 ** currency.exponent),
        revenue_category: form.revenue_category,
        effective_from: form.effective_from,
      },
    });
    setAdding(false);
    onDone('The rate is on the card.');
  });

  const rows = query.data?.rates ?? [];

  return (
    <>
      {adding && (
        <Card title="A Price">
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>Sport</span>
              <select
                value={form.sport_id}
                onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
              >
                <option value="">Choose…</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>What</span>
              <select
                value={form.item}
                onChange={(e) => setForm({ ...form, item: e.target.value })}
              >
                {Object.entries(ITEM_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Who</span>
              <select
                value={form.rate_class}
                onChange={(e) => setForm({ ...form, rate_class: e.target.value })}
              >
                <option value="member">Member</option>
                <option value="guest">Guest</option>
                <option value="outside">Outside play</option>
              </select>
            </label>
            <label className="field">
              <span>Per</span>
              <select
                value={form.unit}
                onChange={(e) => setForm({ ...form, unit: e.target.value })}
              >
                {Object.entries(UNIT_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Amount</span>
              <input
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="9.00"
              />
            </label>
            <label className="field">
              <span>Posts to</span>
              <select
                value={form.revenue_category}
                onChange={(e) => setForm({ ...form, revenue_category: e.target.value })}
              >
                <option value="">Choose…</option>
                {(categories.data?.categories ?? []).map((c) => (
                  <option key={c.revenue_category} value={c.revenue_category}>
                    {c.revenue_category}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>In force from</span>
              <input
                type="date"
                value={form.effective_from}
                onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
              />
            </label>
          </div>
          <div className="hint">
            The category has to be one the club has mapped on the chart of accounts, or the
            charge is refused — which a Saturday morning is the wrong time to find out.
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button disabled={create.busy} onClick={() => create.run()}>
              {create.busy ? 'Working…' : 'Add the rate'}
            </button>
            <button className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card
        title="What the Range Charges"
        actions={
          canEdit && !adding && sports.length > 0 ? (
            <button className="secondary small" onClick={() => setAdding(true)}>
              Add a rate
            </button>
          ) : undefined
        }
        flush
      >
        <ErrorBanner error={query.error} />
        {query.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>Nothing on the card yet. Until there is, nothing can be handed over.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>What</th>
                <th style={{ width: 120 }}>Who</th>
                <th style={{ width: 150 }}>Per</th>
                <th style={{ width: 110 }}>Price</th>
                <th style={{ width: 170 }}>Posts to</th>
                <th style={{ width: 110 }}>In force</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={r.in_force ? undefined : { opacity: 0.55 }}>
                  <td>
                    {ITEM_LABEL[r.item] ?? r.item}
                    {r.field_name && <div className="muted small">{r.field_name}</div>}
                  </td>
                  <td className="muted small">
                    {r.rate_class}
                    {r.day_type ? ` · ${r.day_type}` : ''}
                  </td>
                  <td className="muted small">{UNIT_LABEL[r.unit] ?? r.unit}</td>
                  <td className="mono">{money(r.amount_minor, currency)}</td>
                  <td className="mono small">{r.revenue_category}</td>
                  <td>
                    {r.in_force ? (
                      <Status tone="good">Now</Status>
                    ) : (
                      <span className="muted small">
                        {r.effective_to ? `to ${r.effective_to}` : `from ${r.effective_from}`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

function RackTab({
  query,
  sports,
  canEdit,
  onDone,
}: {
  query: ReturnType<typeof useApi<{ equipment: Equipment[] }>>;
  sports: Sport[];
  canEdit: boolean;
  onDone: (m: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [serial, setSerial] = useState<Record<string, string>>({});
  const [form, setForm] = useState({
    sport_id: '',
    code: '',
    name: '',
    kind: 'firearm',
    gauge: '',
    serial_number: '',
  });

  const create = useMutation(async () => {
    await api('/v1/shooting/equipment', {
      method: 'POST',
      body: {
        sport_id: form.sport_id || null,
        code: form.code.trim(),
        name: form.name.trim(),
        kind: form.kind,
        gauge: form.gauge.trim() || null,
        serial_number: form.serial_number.trim() || null,
        rate_item: form.kind === 'firearm' ? 'loaner_firearm' : 'loaner_ppe',
      },
    });
    setAdding(false);
    onDone('It is on the rack.');
  });

  const reveal = useMutation(async (id: string) => {
    const r = await api<{ serial_number: string | null }>(`/v1/shooting/equipment/${id}/serial`);
    setSerial((s) => ({ ...s, [id]: r.serial_number ?? 'none recorded' }));
  });

  const rows = query.data?.equipment ?? [];

  return (
    <>
      {adding && (
        <Card title="Something the Club Lends">
          <ErrorBanner error={create.error} />
          <div className="grid-2">
            <label className="field">
              <span>Kind</span>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
              >
                <option value="firearm">Firearm</option>
                <option value="eye_protection">Eye protection</option>
                <option value="ear_protection">Ear protection</option>
                <option value="vest">Vest</option>
                <option value="cart">Cart</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="field">
              <span>Sport</span>
              <select
                value={form.sport_id}
                onChange={(e) => setForm({ ...form, sport_id: e.target.value })}
              >
                <option value="">Any sport</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Code</span>
              <input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="CG-1"
              />
            </label>
            <label className="field">
              <span>Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Club Gun 1 — Beretta 686"
              />
            </label>
            {form.kind === 'firearm' && (
              <>
                <label className="field">
                  <span>Gauge</span>
                  <input
                    value={form.gauge}
                    onChange={(e) => setForm({ ...form, gauge: e.target.value })}
                    placeholder="12"
                  />
                </label>
                <label className="field">
                  <span>Serial number</span>
                  <input
                    value={form.serial_number}
                    onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                  />
                </label>
              </>
            )}
          </div>
          <div className="hint">
            A serial number is kept off every list in the product and read one at a time by a
            manager, and each read is written down.
          </div>
          <div className="actions" style={{ marginTop: 12 }}>
            <button disabled={create.busy} onClick={() => create.run()}>
              {create.busy ? 'Working…' : 'Add it'}
            </button>
            <button className="secondary" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </Card>
      )}

      <Card
        title="The Rack"
        actions={
          canEdit && !adding ? (
            <button className="secondary small" onClick={() => setAdding(true)}>
              Add something
            </button>
          ) : undefined
        }
        flush
      >
        <ErrorBanner error={query.error ?? reveal.error} />
        {query.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>
            Nothing yet. The eyes and ears belong here as much as the guns — the glasses are the
            ones a visitor is standing behind the line without.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th style={{ width: 100 }}>Code</th>
                <th>Item</th>
                <th style={{ width: 120 }}>Status</th>
                <th style={{ width: 230 }}>Out to</th>
                <th style={{ width: 170 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} style={e.status === 'retired' ? { opacity: 0.55 } : undefined}>
                  <td className="mono">{e.code}</td>
                  <td>
                    {e.name}
                    <div className="muted small">
                      {e.kind.replace('_', ' ')}
                      {e.gauge ? ` · ${e.gauge} gauge` : ''}
                    </div>
                  </td>
                  <td>
                    {e.status === 'available' ? (
                      <Status tone="good">In the rack</Status>
                    ) : e.status === 'lost' ? (
                      <Status tone="critical">Lost</Status>
                    ) : e.status === 'maintenance' ? (
                      <Status tone="warning">With the gunsmith</Status>
                    ) : (
                      <Status tone="neutral">Retired</Status>
                    )}
                  </td>
                  <td>
                    {e.out_to ? (
                      <>
                        {e.out_to}
                        {e.overdue && (
                          <div>
                            <Status tone="serious">Overdue</Status>
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="muted small">—</span>
                    )}
                  </td>
                  <td className="actions">
                    {canEdit && e.has_serial && (
                      serial[e.id] ? (
                        <span className="mono small">{serial[e.id]}</span>
                      ) : (
                        <button
                          className="secondary small"
                          disabled={reveal.busy}
                          onClick={() => reveal.run(e.id)}
                        >
                          Show serial
                        </button>
                      )
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Which holes the shot goes over.
 *
 * Read-only here on purpose. Setting an exposure window is a judgement
 * about a specific piece of ground and a specific pace of play, and the
 * person who has it is standing on the seventh rather than sitting at
 * this screen. What this does is show what the club has recorded, and
 * say plainly when it has recorded nothing — which is the state that
 * quietly leaves a fourball in a fall zone.
 */
function GolfTab({ query }: { query: ReturnType<typeof useApi<{ conflicts: Conflict[] }>> }) {
  const rows = query.data?.conflicts ?? [];

  return (
    <Card title="Where the Range and the Course Meet" flush>
      <ErrorBanner error={query.error} />
      {query.loading ? (
        <div style={{ padding: 16 }}>
          <Loading rows={3} />
        </div>
      ) : rows.length === 0 ? (
        <Empty>
          Nothing recorded. If any of the club's fields shoot over the course, the range cannot
          protect the holes it does not know about — and nobody will notice until a Saturday.
        </Empty>
      ) : (
        <table className="data">
          <thead>
            <tr>
              <th style={{ width: 150 }}>Field</th>
              <th style={{ width: 90 }}>Hole</th>
              <th style={{ width: 190 }}>A group is there</th>
              <th style={{ width: 150 }}>While hot</th>
              <th>What a member reads</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td>
                  {c.field_name}
                  <div className="muted small">{c.course_name}</div>
                </td>
                <td className="mono">
                  {c.playing_number ?? 'All'}
                  {c.par ? <div className="muted small">par {c.par}</div> : null}
                </td>
                <td className="mono small">
                  {c.exposure_from_minutes}–{c.exposure_to_minutes} min after the first tee
                </td>
                <td>
                  {c.blocks_tee_times ? (
                    <Status tone="serious">Tee times go</Status>
                  ) : (
                    <Status tone="warning">Notice only</Status>
                  )}
                </td>
                <td className="muted small">{c.note ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  busy,
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  busy?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  if (!armed) {
    return (
      <button className="secondary small" onClick={() => setArmed(true)}>
        {label}
      </button>
    );
  }
  return (
    <>
      <button className="danger small" disabled={busy} onClick={onConfirm}>
        {busy ? 'Working…' : confirmLabel}
      </button>
      <button className="secondary small" onClick={() => setArmed(false)}>
        No
      </button>
    </>
  );
}
